/**
 * Dev-only bridge: the webpack server queues commands, this poller runs them inside
 * the Word task pane (clicks + Word.run). Used by `npm run mcc:agent`.
 */

import { log } from '../utils/logging';
import { normalizeWordParagraphBaseline } from '../utils/bidiText';
import {
  cardActionSignals,
  cardLooksSubmitted,
  findAiSourceBocButton,
  findClauseCard,
  findQuickResponseButton,
  findQuickResponseOption,
  findSubmitButton,
  findUndoButton,
  foldLabel,
  isSubmitToResponseLabel,
  isUnusableQrCard,
  previewLooksLikeAiSuggestion,
} from './mccAgentCardDom';
import {
  evaluateSeeProposedApplyFromSnapshot,
  evaluateSeeProposedPreview,
  foldQrCheckText,
} from './mccAgentQuickResponseCheck';
import { getDocumentParagraphsForSkill } from '../features/agent/skills/SkillWordExecutor';
import { applyHeadingStructureToInsertedText } from '../word/api/AgentDocumentInserter';

/* global Word */

const PREFIX = '[MccAgent]'; // accepted-ooxml-v1 poller

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function foldWs(text: string): string {
  return normalizeWordParagraphBaseline(text || '').replace(/\s+/g, ' ').trim();
}

function findCard(clauseNumber: string): HTMLElement | null {
  return findClauseCard(document, clauseNumber);
}

function requireCard(clauseNumber: string): HTMLElement {
  const card = findCard(clauseNumber);
  if (card) return card;
  const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="cc-clause-card"]'));
  const ids = cards.map((c) => c.getAttribute('data-clause-id') || '');
  throw new Error(
    `clause card ${clauseNumber} not found (${cards.length} cards); idsMatching=${ids.filter((id) => id.includes(clauseNumber)).join(',') || 'none'}`,
  );
}

async function waitFor<T>(
  pred: () => T | null | false | undefined,
  timeoutMs: number,
  label: string,
): Promise<T> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const value = pred();
    if (value) return value;
    await sleep(250);
  }
  throw new Error(`timeout waiting for ${label}`);
}

type TableParent = {
  isNullObject?: boolean;
  load: (s: string | string[]) => void;
  rowCount?: number;
};

async function snapshotSelectionParagraph(): Promise<Record<string, unknown>> {
  if (typeof Word === 'undefined') {
    return { office: false };
  }
  return Word.run(async (context) => {
    const tables = context.document.body.tables;
    tables.load('items');
    const para = context.document.getSelection().paragraphs.getFirst();
    para.load('text');
    const parentTable = (para as unknown as { parentTableOrNullObject?: TableParent }).parentTableOrNullObject;
    if (parentTable) parentTable.load('isNullObject, rowCount');
    let trackedChangeCount: number | null = null;
    try {
      const trackedChanges = (para as unknown as { getTrackedChanges?: () => { load: (s: string) => void; items?: unknown[] } }).getTrackedChanges?.();
      if (trackedChanges) {
        trackedChanges.load('items');
      }
      await context.sync();
      trackedChangeCount = trackedChanges ? trackedChanges.items?.length ?? 0 : null;
    } catch {
      await context.sync();
    }
    const inTable = parentTable ? parentTable.isNullObject === false : false;
    const text = foldWs(para.text || '');
    return {
      office: true,
      tableCount: tables.items.length,
      hit: text.length > 0,
      inTable,
      tableRowCount: inTable ? parentTable?.rowCount ?? null : null,
      trackedChangeCount,
      textLen: text.length,
      textHead: text.slice(0, 80),
      textTail: text.slice(-80),
      contains2500: text.includes('2500'),
      containsRegistrationFees: text.includes('הוצאות הרישום'),
    };
  });
}

async function snapshotCardParagraph(card: HTMLElement): Promise<Record<string, unknown>> {
  const header = card.querySelector<HTMLElement>('.cc-clause-header');
  header?.click();
  // Content search can run several Word.run attempts (full + tail) before select() settles.
  await sleep(4000);
  return snapshotSelectionParagraph();
}

function liveCard(clauseNumber: string): HTMLElement {
  return requireCard(clauseNumber);
}

function enabledUndoButton(clauseNumber: string): HTMLButtonElement | null {
  const card = findCard(clauseNumber);
  if (!card) return null;
  const undo = findUndoButton(card);
  if (!undo || undo.disabled) return null;
  return undo;
}

function suggestionReady(card: HTMLElement): boolean {
  if (card.getAttribute('data-ai-streaming') === 'true') return false;
  const submit = findSubmitButton(card);
  return previewLooksLikeAiSuggestion(previewColorStats(card)) && Boolean(submit && !submit.disabled);
}

function previewPlainText(card: HTMLElement): string {
  const root = card.querySelector('.cc-selected-text-xml') || card;
  return foldQrCheckText(root.textContent || '');
}

function previewInsTexts(card: HTMLElement): string[] {
  const root = card.querySelector('.cc-selected-text-xml') || card;
  return Array.from(root.querySelectorAll('ins')).map((el) => foldQrCheckText(el.textContent || ''));
}

function previewColorStats(card: HTMLElement): Record<string, unknown> {
  const root = card.querySelector('.cc-selected-text-xml') || card;
  const ins = Array.from(root.querySelectorAll('ins'));
  const del = Array.from(root.querySelectorAll('del'));
  const isNew = (el: Element) => el.classList.contains('track-change-new');
  return {
    insTotal: ins.length,
    insBlue: ins.filter(isNew).length,
    insRed: ins.filter((el) => !isNew(el)).length,
    delTotal: del.length,
    delBlue: del.filter(isNew).length,
    delRed: del.filter((el) => !isNew(el)).length,
    htmlHasTrackChangeNew: (root.innerHTML || '').includes('track-change-new'),
    previewHtmlLen: (root.innerHTML || '').length,
  };
}

async function runSnapshot(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const card = liveCard(clauseNumber);
  card.scrollIntoView({ block: 'center' });
  await sleep(200);
  const afterScroll = liveCard(clauseNumber);
  const snap = await snapshotCardParagraph(afterScroll);
  const fresh = liveCard(clauseNumber);
  const actions = cardActionSignals(fresh);
  const aiBtn = fresh.querySelector<HTMLButtonElement>('[data-testid="cc-ai-btn"]');
  return {
    clauseNumber,
    clauseId: fresh.getAttribute('data-clause-id'),
    aiEnabled: Boolean(aiBtn && !aiBtn.disabled),
    preview: previewColorStats(fresh),
    submitted: cardLooksSubmitted(fresh),
    streaming: fresh.getAttribute('data-ai-streaming') === 'true',
    hasPreview: Boolean(
      fresh.querySelector('ins.track-change-new, ins.track-change-insertion, del.track-change-del, del.track-change-deletion'),
    ),
    hasSubmit: Boolean(actions.hasSubmit),
    hasUndo: Boolean(actions.hasUndo),
    actions,
    ...snap,
  };
}

async function runQuickResponse(args: {
  clauseNumber?: string;
  type?: string;
}): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const optionId = String(args.type || 'see_proposed_correction');
  const card = requireCard(clauseNumber);
  if (cardLooksSubmitted(card)) {
    throw new Error('quick-response refused: card already submitted — undo-response first');
  }
  card.scrollIntoView({ block: 'center' });
  await sleep(800);

  // Leftover QR/AI preview makes baselineText already contain the suffix, so a
  // second click is a no-op and submit never grows the live paragraph. Always
  // clear when possible so the paint wait sees a real delta.
  const clearBtn = liveCard(clauseNumber).querySelector<HTMLButtonElement>('[data-testid="cc-clear-ai"]');
  if (clearBtn && !clearBtn.disabled) {
    clearBtn.click();
    await sleep(500);
  }

  const baselinePreview = previewColorStats(liveCard(clauseNumber));
  const previewBaselineText = previewPlainText(liveCard(clauseNumber));
  // Short clauses (party lines / headings) often show a longer TC/CR string in
  // the card while Word live text is the short paragraph. Apply checks must use
  // the live Word baseline or they false-fail with apply_lost_baseline_head.
  let baselineText = previewBaselineText;
  try {
    const liveSnap = await snapshotCardParagraph(liveCard(clauseNumber));
    const liveLen = Number(liveSnap.textLen) || 0;
    const liveHead = String(liveSnap.textHead || '');
    const liveTail = String(liveSnap.textTail || '');
    if (
      liveLen > 0 &&
      liveHead &&
      liveTail &&
      liveHead === liveTail &&
      liveLen === liveHead.length
    ) {
      baselineText = liveHead;
    }
  } catch {
    // keep preview baseline
  }
  const qrBtn = findQuickResponseButton(liveCard(clauseNumber));
  if (!qrBtn) throw new Error('quick-response button missing');
  const live = liveCard(clauseNumber);
  if (qrBtn.disabled || isUnusableQrCard(live)) {
    return {
      ok: true,
      skipped: true,
      reason: qrBtn.disabled
        ? 'quick-response disabled (no client request)'
        : 'no client request / clause not found',
      clauseNumber,
      clauseId: live.getAttribute('data-clause-id'),
      baselineText: baselineText.slice(0, 120),
    };
  }

  let clickedOption = false;
  for (let attempt = 1; attempt <= 2; attempt++) {
    findQuickResponseButton(liveCard(clauseNumber))?.click();
    try {
      const option = await waitFor(
        () => findQuickResponseOption(document, optionId),
        2500,
        `quick-response option ${optionId}`,
      );
      option.click();
      clickedOption = true;
      break;
    } catch {
      // Toggle / scroll may have closed the portaled menu.
    }
  }
  if (!clickedOption) {
    throw new Error(`quick-response menu did not stay open for ${optionId}`);
  }

  await waitFor(() => {
    const current = findCard(clauseNumber);
    if (!current) return null;
    const stats = previewColorStats(current);
    const text = previewPlainText(current);
    const changed =
      stats.htmlHasTrackChangeNew ||
      Number(stats.insBlue) > Number(baselinePreview.insBlue) ||
      text.includes('ראה תיקון מוצע');
    return changed ? current : null;
  }, 30000, 'quick-response preview painted');

  const ready = liveCard(clauseNumber);
  const preview = previewColorStats(ready);
  const previewText = previewPlainText(ready);
  const insTexts = previewInsTexts(ready);
  const checks = evaluateSeeProposedPreview({
    previewText,
    baselineText,
    insBlue: Number(preview.insBlue) || 0,
    htmlHasTrackChangeNew: preview.htmlHasTrackChangeNew === true,
    insTexts,
  });
  log(`${PREFIX} quick-response`, {
    clauseNumber,
    optionId,
    checks,
    previewText: previewText.slice(-80),
  });
  return {
    ok: true,
    skipped: false,
    clauseNumber,
    clauseId: ready.getAttribute('data-clause-id'),
    optionId,
    clickedOption,
    baselineText,
    previewText,
    preview,
    insTexts,
    checks,
    previewOk: checks.ok,
  };
}

async function runClear(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const card = requireCard(clauseNumber);
  card.scrollIntoView({ block: 'center' });
  await sleep(200);
  const clearBtn = card.querySelector<HTMLButtonElement>('[data-testid="cc-clear-ai"]');
  if (!clearBtn) {
    return { ok: true, clauseNumber, cleared: false, reason: 'clear button not present' };
  }
  clearBtn.click();
  await sleep(300);
  const preview = previewColorStats(card);
  log(`${PREFIX} cleared`, { clauseNumber, clauseId: card.getAttribute('data-clause-id'), preview });
  return { ok: true, clauseNumber, cleared: true, clauseId: card.getAttribute('data-clause-id'), preview };
}

function cardIsStreaming(clauseNumber: string): boolean {
  return findCard(clauseNumber)?.getAttribute('data-ai-streaming') === 'true';
}

/**
 * AI on a card with both BOC and past-negotiation sources only opens a portaled
 * menu. scrollIntoView can still emit scroll after that click, and
 * useFixedDropdownPosition closes the menu on scroll — so a fixed 400ms wait
 * often finds no BOC button and never starts generation.
 */
async function clickAiAndStartGeneration(clauseNumber: string): Promise<{
  clickedBoc: boolean;
  sawMenu: boolean;
  attempts: number;
}> {
  let sawMenu = false;
  let clickedBoc = false;
  for (let attempt = 1; attempt <= 2; attempt++) {
    const card = liveCard(clauseNumber);
    const aiBtn = card.querySelector<HTMLButtonElement>('[data-testid="cc-ai-btn"]');
    if (!aiBtn || aiBtn.disabled) {
      throw new Error(`AI button missing or disabled (attempt ${attempt})`);
    }
    aiBtn.click();
    try {
      const boc = await waitFor(() => {
        if (cardIsStreaming(clauseNumber)) return 'streaming' as const;
        const btn = findAiSourceBocButton(document);
        return btn && !btn.disabled ? btn : null;
      }, 2000, 'AI source menu or streaming');
      if (boc === 'streaming') {
        return { clickedBoc, sawMenu, attempts: attempt };
      }
      sawMenu = true;
      boc.click();
      clickedBoc = true;
      return { clickedBoc, sawMenu, attempts: attempt };
    } catch {
      // First click may have closed an already-open menu (toggle). Retry once.
    }
  }
  throw new Error(
    'AI click did not open the source menu and did not start streaming. The menu may have closed on scroll.',
  );
}

async function runAi(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const card = requireCard(clauseNumber);
  card.scrollIntoView({ block: 'center' });
  // Let the clause list finish scrolling before opening the AI menu — a late
  // scroll event closes the portaled source menu without starting generation.
  await sleep(800);

  const baselinePreview = previewColorStats(liveCard(clauseNumber));
  const start = await clickAiAndStartGeneration(clauseNumber);
  log(`${PREFIX} ai clicked`, { clauseNumber, ...start });

  // Existing CR redlines make suggestionReady() a false positive. Require a real stream.
  await waitFor(
    () => (cardIsStreaming(clauseNumber) ? true : null),
    20000,
    'AI streaming start',
  );

  await waitFor(() => {
    const current = findCard(clauseNumber);
    if (!current || current.getAttribute('data-ai-streaming') === 'true') return null;
    const clear = current.querySelector('[data-testid="cc-clear-ai"]');
    const stats = previewColorStats(current);
    const changed =
      stats.htmlHasTrackChangeNew ||
      stats.previewHtmlLen !== baselinePreview.previewHtmlLen ||
      stats.insBlue !== baselinePreview.insBlue ||
      stats.delTotal !== baselinePreview.delTotal;
    return clear && changed ? current : null;
  }, 90000, 'AI suggestion finished (stream ended + preview changed)');
  const readyCard = liveCard(clauseNumber);
  const previewAtReady = previewColorStats(readyCard);
  log(`${PREFIX} ai suggestionReady (pre-hybrid-wait)`, {
    clauseNumber,
    sawStreaming: true,
    ...start,
    baselinePreview,
    previewAtReady,
  });
  // Hybrid preview is debounced 350ms + a Word.run; the blue swap happens after this.
  await sleep(3500);
  const previewAfterHybrid = previewColorStats(liveCard(clauseNumber));
  log(`${PREFIX} ai after hybrid window`, { clauseNumber, previewAtReady, previewAfterHybrid });
  return {
    ok: true,
    clauseNumber,
    clauseId: liveCard(clauseNumber).getAttribute('data-clause-id'),
    suggestionReady: true,
    sawStreaming: true,
    clickedBoc: start.clickedBoc,
    sawMenu: start.sawMenu,
    baselinePreview,
    previewAtReady,
    previewAfterHybrid,
  };
}

function submitHandlerStarted(clauseNumber: string): boolean {
  const card = findCard(clauseNumber);
  if (!card) return false;
  if (card.getAttribute('data-response-submitting') === 'true') return true;
  if (card.getAttribute('data-response-submitted') === 'true') return true;
  const submit = findSubmitButton(card);
  const label = foldLabel(submit?.textContent || '');
  if (isSubmitToResponseLabel(label) && (label.includes('מחיל') || label.includes('applying'))) {
    return true;
  }
  if (findUndoButton(card) && !findUndoButton(card)!.disabled) return true;
  if (label.includes('לא ניתן') || label.includes('could not apply')) return true;
  return false;
}

/**
 * Click שלח לתגובה on the live card. Do not snapshot via the clause header
 * first — that click is go-to, steals 4s, and looks like the agent never
 * pressed the submit button.
 */
async function clickSubmitToResponse(clauseNumber: string): Promise<Record<string, unknown>> {
  const card = liveCard(clauseNumber);
  const submit = card.querySelector<HTMLButtonElement>('[data-testid="cc-submit-to-response"]')
    || findSubmitButton(card);
  if (!submit) {
    throw new Error(`submit button missing ${JSON.stringify(cardActionSignals(card))}`);
  }
  if (submit.disabled) {
    throw new Error(`submit button disabled ${JSON.stringify(cardActionSignals(card))}`);
  }
  const label = (submit.textContent || '').replace(/\s+/g, ' ').trim();
  if (!isSubmitToResponseLabel(label)) {
    throw new Error(`refusing to click non-submit button label=${JSON.stringify(label)}`);
  }
  submit.scrollIntoView({ block: 'center', inline: 'nearest' });
  await sleep(200);
  const live = liveCard(clauseNumber).querySelector<HTMLButtonElement>('[data-testid="cc-submit-to-response"]')
    || findSubmitButton(liveCard(clauseNumber));
  if (!live || live.disabled) {
    throw new Error('submit button disappeared or disabled after scroll');
  }
  live.click();
  const clickInfo = {
    clauseId: liveCard(clauseNumber).getAttribute('data-clause-id'),
    testid: live.getAttribute('data-testid'),
    label,
    preview: previewColorStats(liveCard(clauseNumber)),
  };
  log(`${PREFIX} submit clicked`, clickInfo);
  return clickInfo;
}

async function runSubmit(args: { clauseNumber?: string; baselineText?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const card = requireCard(clauseNumber);
  if (!suggestionReady(card)) {
    throw new Error('submit refused: AI suggestion is not ready yet');
  }

  const clickInfo = await clickSubmitToResponse(clauseNumber);
  try {
    await waitFor(
      () => (submitHandlerStarted(clauseNumber) ? true : null),
      4000,
      'submit handler started (מחיל / data-response-submitting)',
    );
  } catch (err) {
    throw new Error(
      `${err instanceof Error ? err.message : String(err)} ${JSON.stringify({
        ...clickInfo,
        actions: cardActionSignals(liveCard(clauseNumber)),
      })}`,
    );
  }

  try {
    await waitFor(
      () => enabledUndoButton(clauseNumber),
      90000,
      'undo button after apply finished',
    );
  } catch (err) {
    const fresh = liveCard(clauseNumber);
    const lastStage =
      typeof window !== 'undefined' ? (window as any).__mccLastQrStage : undefined;
    const lastStages =
      typeof window !== 'undefined' ? (window as any).__mccLastQrStages : undefined;
    throw new Error(
      `${err instanceof Error ? err.message : String(err)} ${JSON.stringify({
        clickLanded: true,
        actions: cardActionSignals(fresh),
        preview: previewColorStats(fresh),
        submitting: fresh.getAttribute('data-response-submitting'),
        lastQrStage: lastStage ?? null,
        lastQrStagesTail: Array.isArray(lastStages) ? lastStages.slice(-8) : null,
      })}`,
    );
  }

  const afterApply = await snapshotSelectionParagraph();
  const actions = cardActionSignals(liveCard(clauseNumber));
  const baselineText = String(args.baselineText || '');
  const applyChecks = baselineText
    ? evaluateSeeProposedApplyFromSnapshot({
        textHead: String(afterApply.textHead || ''),
        textTail: String(afterApply.textTail || ''),
        textLen: Number(afterApply.textLen) || 0,
        baselineText,
        hasUndo: Boolean(actions.hasUndo),
        tableOk: afterApply.tableCount == null ? undefined : true,
      })
    : undefined;
  const result = {
    ok: true,
    clauseNumber,
    clauseId: liveCard(clauseNumber).getAttribute('data-clause-id'),
    clickLanded: true,
    clickInfo,
    afterApply,
    actions,
    applyChecks,
    applyOk: applyChecks ? applyChecks.ok : undefined,
  };
  log(`${PREFIX} submit`, result);
  return result;
}

/**
 * NOT YET SUPPORTED, deliberately: replacing a track-changes-mode client-request
 * edit box's (`.cc-selected-text-xml--editable`) entire content programmatically.
 * Two approaches were tried live against a real card and both were dead ends —
 * don't repeat either without a new idea:
 *
 * 1. Select-all (`Range.selectNodeContents` + `Selection.addRange`) then
 *    dispatch a manually constructed `new InputEvent('beforeinput', {
 *    inputType: 'insertFromPaste', data: text })` — the exact shape
 *    `handleCrTrackChangesBeforeInput` handles for a real paste
 *    (`isTrackChangesClipboardInputType`), and architecturally the RIGHT
 *    path (it resolves the replaced range from the selection BEFORE any
 *    mutation, unlike approach 2). Never reached React's handler —
 *    `event.defaultPrevented` stayed `false` after dispatch, meaning this
 *    app's onBeforeInput wiring does not respond to a synthetically
 *    constructed/dispatched `beforeinput` here (React event-plugin
 *    internals, not this app's code. DIAGNOSED with `runProbeBeforeinput`
 *    below (side-effect-free): a temporary native `document.addEventListener
 *    ('beforeinput', ..., true)` DOES see the dispatched event, with the
 *    right `inputType`/`data`, proving dispatch mechanics are fine — yet
 *    `event.defaultPrevented` still stays `false`, meaning React's handler
 *    still never runs even once the event data is verifiably correct. Most
 *    likely cause: React's beforeinput handling only responds to TRUSTED
 *    events (`isTrusted: true`, a read-only, unspoofable browser-computed
 *    property for anything dispatched via `dispatchEvent`) — a real
 *    platform boundary, not a bug in this approach to fix further.
 *    (Also separately found+worked around: this WebView's `InputEvent`
 *    constructor was not applying `inputType` from the options dict at all —
 *    came back as `''` — even though `data` did stick; forcing it via
 *    `Object.defineProperty(ev, 'inputType', {value: ...})` fixed THAT part,
 *    but did not change the `defaultPrevented` result above.)
 * 2. `document.execCommand('insertText', false, text)` after the same
 *    select-all — makes the browser itself perform the edit, which DOES
 *    fire real listeners (`handleCrTrackChangesEditInput`, the keyboard
 *    'input'-event path). But that path exists specifically to reconstruct
 *    a selection the browser already collapsed by the time 'input' fires,
 *    using `crEditPendingSelectionRef`/`crEditPendingAcceptedRef` — private
 *    React refs populated by `handleCrTrackChangesKeyDown` on the PRECEDING
 *    keydown, which execCommand never fires. Verified live on a real clause
 *    (13.6): the result was NOT a replace — `delTotal: 0`, the live
 *    paragraph grew from 677 to 847 chars with the old tail content still
 *    present alongside the new text. This got submitted (בטל תגובה appeared)
 *    before being caught and reverted (`undo-response` + `restore-original`,
 *    back to the exact original 677-char baseline) — a real, if fully
 *    reversible, live corruption. Do not use `execCommand('insertText')` for
 *    a bulk replace against this edit box again without first solving the
 *    pendingSelection problem.
 */

/**
 * Reproduce the "manually edit the client request, save, then שלח לתגובה"
 * path — distinct from `submit`, which requires an AI/QR ins-del preview
 * (`suggestionReady`) before clicking. A plain manual edit never renders that
 * preview markup, so `submit`'s precondition would always (wrongly) refuse
 * it; this drives the same Edit → type/paste → Save → שלח לתגובה flow a
 * lawyer actually uses, then reuses `clickSubmitToResponse`/
 * `submitHandlerStarted` to finish the apply the same way `submit` does.
 *
 * Only supports the plain-textarea edit mode (`.cc-edit-textarea`, no prior
 * AI suggestion / track-changes markup on this client request). A card with
 * an existing AI suggestion or track-changes markup renders a contentEditable
 * box instead (`.cc-selected-text-xml--editable`, `canEditWithTrackChangesPreview`
 * true) — deliberately refused rather than attempted: see the comment block
 * just above this one for the two approaches tried live and why both are
 * dead ends (one silently does nothing, the other produced a real — if fully
 * reversible — corrupted live edit). Pick a plain client-request card to
 * test this path until that gap is actually closed. On refusal this clicks
 * Cancel (ביטול) first so the card does not visibly stay stuck in edit mode.
 */
/**
 * One-shot, side-effect-free diagnostic for the open question in the doc
 * comment above: does this app's onBeforeInput prop respond to a manually
 * constructed+dispatched native `beforeinput` event at all? Dispatching an
 * `InputEvent` object by itself never mutates contentEditable content (only
 * `execCommand` or a real keystroke does that) — so this is safe to run
 * against the real live card with zero mutation risk, unlike the two
 * approaches already tried and reverted. Adds a temporary native
 * document-level capture listener (removed immediately after) to tell apart
 * "the event never reached the DOM root at all" from "it reached the root
 * but React's handler didn't respond to it."
 */
async function runProbeBeforeinput(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  let card = requireCard(clauseNumber);
  const editBtn = card.querySelector<HTMLButtonElement>('.cc-btn-edit');
  if (editBtn && !card.querySelector('.cc-edit-textarea, .cc-selected-text-xml--editable')) {
    editBtn.click();
    await sleep(300);
  }
  card = requireCard(clauseNumber);
  const contentEditable = card.querySelector<HTMLElement>('.cc-selected-text-xml--editable');
  if (!contentEditable) {
    const cancelBtn = card.querySelector<HTMLButtonElement>('.cc-btn-cancel');
    cancelBtn?.click();
    throw new Error('probe-beforeinput requires this card to enter track-changes contentEditable edit mode (found plain textarea or nothing instead) — pick a clause with existing AI/track-changes markup');
  }

  let nativeListenerSaw: { inputType: string; data: string | null } | null = null;
  const nativeListener = (e: Event) => {
    const ie = e as InputEvent;
    nativeListenerSaw = { inputType: ie.inputType, data: ie.data };
  };
  document.addEventListener('beforeinput', nativeListener, true);

  const before = (contentEditable.textContent || '').slice(0, 80);
  contentEditable.focus();
  const sel = window.getSelection();
  if (sel) {
    const range = document.createRange();
    range.selectNodeContents(contentEditable);
    sel.removeAllRanges();
    sel.addRange(range);
  }
  await sleep(50);
  const ev = new InputEvent('beforeinput', {
    inputType: 'insertFromPaste',
    data: '__PROBE_MARKER__',
    bubbles: true,
    cancelable: true,
    composed: true,
  });
  const inputTypeStuckFromConstructor = ev.inputType === 'insertFromPaste';
  if (!inputTypeStuckFromConstructor) {
    // This WebView's InputEvent constructor is not applying `inputType` from
    // the options dict (observed: comes back as ''), even though `data` does
    // stick — force it directly on the instance as a fallback.
    try {
      Object.defineProperty(ev, 'inputType', { value: 'insertFromPaste', configurable: true });
    } catch {
      // ignore — worst case the probe just reports the constructor result below
    }
  }
  const dispatchReturnValue = contentEditable.dispatchEvent(ev);
  await sleep(100);
  document.removeEventListener('beforeinput', nativeListener, true);

  const result = {
    ok: true,
    inputTypeStuckFromConstructor,
    nativeDomListenerSawEvent: nativeListenerSaw !== null,
    nativeListenerData: nativeListenerSaw,
    dispatchReturnValue,
    eventDefaultPrevented: ev.defaultPrevented,
    textUnchangedAfterDispatch: (contentEditable.textContent || '').slice(0, 80) === before,
  };

  // Leave the card the way we found it — this probe only entered edit mode
  // to test the event, nothing here is meant to be kept.
  const cancelBtn = requireCard(clauseNumber).querySelector<HTMLButtonElement>('.cc-btn-cancel');
  cancelBtn?.click();

  return result;
}

/**
 * THIRD approach to the contentEditable replace problem (see the two dead
 * ends documented above `runManualEditSubmit`) — not yet wired into it,
 * kept as its own dry-run-only probe until verified safe on more than one
 * clause: always Cancels out afterward and NEVER clicks Save, so a failed
 * attempt here cannot reach the live Word document at all.
 *
 * Reasoning: `execCommand('insertText')` DOES reach real listeners
 * (confirmed: it visibly mutated content in the reverted 13.6 test), but
 * mis-resolves the replaced range because `crEditPendingSelectionRef`/
 * `crEditPendingAcceptedRef` (private refs `handleCrTrackChangesKeyDown`
 * populates on a REAL preceding keydown) are empty. `inferTrackChangesBeforeInput`
 * (line ~515) prefers `inputEvent.data` over the pending-key fallback
 * whenever `data` is non-empty — and execCommand's resulting native 'input'
 * event DOES carry the full inserted text as `.data` — so the missing piece
 * is ONLY the selection range, not the text content.
 *
 * Fix attempt: dispatch a genuine (if synthetic) `keydown` KeyboardEvent
 * — unlike `beforeinput` (see the probe above), a plain `keydown` dispatch
 * does NOT itself mutate contentEditable content (no default browser text
 * insertion happens from a keydown alone), so this remains safe up to this
 * point — to prime `crEditPendingSelectionRef` with the CURRENT (full,
 * select-all'd) range via `saveAcceptedSelectionRange` before `execCommand`
 * actually performs the real mutation. If `commitCrTrackChangesEdit` (called
 * from `handleCrTrackChangesEditInput` once execCommand's real 'input' event
 * fires) sees a non-collapsed `pendingSelection`, it uses that DIRECTLY
 * (`resolveTrackChangesInputSelection`'s first branch) instead of the
 * caret-guessing fallback that produced the 677→847-char corruption before.
 */
async function runPrimedPasteDryRun(args: {
  clauseNumber?: string;
  text?: string;
}): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const text = String(args.text || '');
  if (!text.trim()) throw new Error('primed-paste-dry-run requires non-empty --text');

  let card = requireCard(clauseNumber);
  const editBtn = card.querySelector<HTMLButtonElement>('.cc-btn-edit');
  if (editBtn && !card.querySelector('.cc-edit-textarea, .cc-selected-text-xml--editable')) {
    editBtn.click();
    await sleep(300);
  }
  card = requireCard(clauseNumber);
  const contentEditable = card.querySelector<HTMLElement>('.cc-selected-text-xml--editable');
  if (!contentEditable) {
    const cancelBtn = card.querySelector<HTMLButtonElement>('.cc-btn-cancel');
    cancelBtn?.click();
    throw new Error('primed-paste-dry-run requires this card to enter track-changes contentEditable edit mode');
  }

  const before = foldWs(contentEditable.textContent || '');

  contentEditable.focus();
  const sel = window.getSelection();
  let selectionDiag: Record<string, unknown> = {};
  if (sel) {
    const range = document.createRange();
    // Element-level boundaries (selectNodeContents) may not walk the same way
    // as a natural user selection through this app's own accepted-offset
    // mapper (`getAcceptedOffsetAtPoint`) — anchor on the actual first/last
    // TEXT nodes instead, matching what a real Ctrl+A selection produces.
    const walker = document.createTreeWalker(contentEditable, NodeFilter.SHOW_TEXT);
    let firstText: Text | null = null;
    let lastText: Text | null = null;
    let node: Node | null;
    // eslint-disable-next-line no-cond-assign
    while ((node = walker.nextNode())) {
      if (!firstText) firstText = node as Text;
      lastText = node as Text;
    }
    if (firstText && lastText) {
      range.setStart(firstText, 0);
      range.setEnd(lastText, lastText.length);
    } else {
      range.selectNodeContents(contentEditable);
    }
    sel.removeAllRanges();
    sel.addRange(range);
    selectionDiag = {
      usedLeafTextNodes: Boolean(firstText && lastText),
      selectionIsCollapsed: sel.isCollapsed,
      selectionTextLen: String(sel.toString() || '').length,
    };
  }
  await sleep(50);

  const keydownEv = new KeyboardEvent('keydown', {
    key: 'a',
    bubbles: true,
    cancelable: true,
    composed: true,
  });
  contentEditable.dispatchEvent(keydownEv);
  await sleep(30);

  const execOk = document.execCommand('insertText', false, text);
  await sleep(200);

  const afterFold = foldWs(requireCard(clauseNumber).querySelector<HTMLElement>('.cc-selected-text-xml--editable')?.textContent || '');
  const targetFold = foldWs(text);
  // Accepted-text extraction can legitimately differ from raw textContent by
  // incidental whitespace/markup — require the target to appear as a
  // substring AND the result to be close in length, not byte-exact, but
  // reject anything that still contains a large remnant of the ORIGINAL
  // (the exact failure mode seen last time: old content left in place).
  const containsTarget = afterFold.includes(targetFold) || targetFold.includes(afterFold);
  const stillContainsOldTail = before.length > 40 && afterFold.includes(before.slice(-40));
  const looksCorrect = containsTarget && !stillContainsOldTail && Math.abs(afterFold.length - targetFold.length) < 20;

  const result = {
    ok: true,
    execOk,
    selectionDiag,
    before: before.slice(0, 80),
    beforeLen: before.length,
    after: afterFold.slice(0, 120),
    afterLen: afterFold.length,
    targetLen: targetFold.length,
    containsTarget,
    stillContainsOldTail,
    looksCorrect,
  };

  // Dry run only — always cancel, never save, regardless of outcome.
  const cancelBtn = requireCard(clauseNumber).querySelector<HTMLButtonElement>('.cc-btn-cancel');
  cancelBtn?.click();

  return result;
}

async function runManualEditSubmit(args: {
  clauseNumber?: string;
  text?: string;
}): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const text = String(args.text || '');
  if (!text.trim()) throw new Error('manual-edit-submit requires non-empty --text');

  let card = requireCard(clauseNumber);
  if (cardLooksSubmitted(card)) {
    throw new Error('manual-edit-submit refused: card already submitted — undo-response first');
  }

  const editBtn = card.querySelector<HTMLButtonElement>('.cc-btn-edit');
  if (editBtn && !card.querySelector('.cc-edit-textarea, .cc-selected-text-xml--editable')) {
    editBtn.click();
    await sleep(300);
  }

  card = requireCard(clauseNumber);
  const textarea = card.querySelector<HTMLTextAreaElement>('.cc-edit-textarea');
  if (!textarea) {
    const contentEditable = card.querySelector('.cc-selected-text-xml--editable');
    // Refuse rather than attempt — see this function's own doc comment and the
    // block above it for why both approaches tried live are dead ends (one is
    // a silent no-op, the other produces a real corrupted edit: verified and
    // reverted live on clause 13.6). Cancel out of edit mode first so the
    // card is not left visibly stuck showing שמירה/ביטול.
    const cancelBtn = card.querySelector<HTMLButtonElement>('.cc-btn-cancel');
    cancelBtn?.click();
    throw new Error(
      `manual-edit-submit only supports plain edit mode (.cc-edit-textarea); found ${
        contentEditable ? 'track-changes contentEditable mode (has AI suggestion / existing markup — pick a plainer clause)' : 'no edit field at all'
      }`,
    );
  }
  const editMode = 'plain' as const;
  setControlledInputValue(textarea, text);
  await sleep(150);

  const saveBtn = card.querySelector<HTMLButtonElement>('.cc-btn-save');
  if (!saveBtn) throw new Error('save button (.cc-btn-save) not found');
  saveBtn.click();

  await waitFor(() => {
    const fresh = findCard(clauseNumber);
    if (!fresh) return null;
    return fresh.querySelector('.cc-edit-textarea, .cc-selected-text-xml--editable') ? null : true;
  }, 8000, 'edit mode closed after save');

  const clickInfo = await clickSubmitToResponse(clauseNumber);
  try {
    await waitFor(
      () => (submitHandlerStarted(clauseNumber) ? true : null),
      4000,
      'submit handler started (מחיל / data-response-submitting)',
    );
  } catch (err) {
    throw new Error(
      `${err instanceof Error ? err.message : String(err)} ${JSON.stringify({
        ...clickInfo,
        actions: cardActionSignals(liveCard(clauseNumber)),
      })}`,
    );
  }

  try {
    await waitFor(() => enabledUndoButton(clauseNumber), 90000, 'undo button after apply finished');
  } catch (err) {
    const fresh = liveCard(clauseNumber);
    throw new Error(
      `${err instanceof Error ? err.message : String(err)} ${JSON.stringify({
        clickLanded: true,
        actions: cardActionSignals(fresh),
        preview: previewColorStats(fresh),
      })}`,
    );
  }

  const afterApply = await snapshotSelectionParagraph();
  const actions = cardActionSignals(liveCard(clauseNumber));
  const result = {
    ok: true,
    clauseNumber,
    clauseId: liveCard(clauseNumber).getAttribute('data-clause-id'),
    editMode,
    editedText: text,
    clickInfo,
    afterApply,
    actions,
  };
  log(`${PREFIX} manual-edit-submit`, result);
  return result;
}

async function runUndoResponse(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  let undo: HTMLButtonElement;
  try {
    undo = await waitFor(() => {
      const current = findCard(clauseNumber);
      if (!current) return null;
      const btn = findUndoButton(current);
      if (btn && !btn.disabled) return btn;
      return null;
    }, 15000, 'enabled undo button');
  } catch (err) {
    const current = findCard(clauseNumber);
    const signals = current ? cardActionSignals(current) : { cardFound: false };
    throw new Error(
      `${err instanceof Error ? err.message : String(err)} ${JSON.stringify(signals)}`,
    );
  }

  const beforeUndo = await snapshotCardParagraph(liveCard(clauseNumber));
  const liveUndo = enabledUndoButton(clauseNumber) || undo;
  liveUndo.click();

  await waitFor(
    () => {
      const current = findCard(clauseNumber);
      if (!current) return null;
      if (cardLooksSubmitted(current)) return null;
      return findSubmitButton(current);
    },
    45000,
    'submit button after undo finished',
  );
  await sleep(600);

  const afterCard = liveCard(clauseNumber);
  const afterUndo = await snapshotCardParagraph(afterCard);
  const tableOk = beforeUndo.tableCount === afterUndo.tableCount;
  const rowsOk =
    beforeUndo.tableRowCount == null || beforeUndo.tableRowCount === afterUndo.tableRowCount;
  const beforeBlob = `${beforeUndo.textHead || ''} ${beforeUndo.textTail || ''}`;
  const afterBlob = `${afterUndo.textHead || ''} ${afterUndo.textTail || ''}`;
  const hadSuffix = /ראה תיקון מוצע/.test(beforeBlob);
  const stillHasSuffix = /ראה תיקון מוצע/.test(afterBlob);
  const textChanged =
    Number(beforeUndo.textLen) !== Number(afterUndo.textLen) || (hadSuffix && !stillHasSuffix);
  // Apply sometimes hit a wrong short cell; the card paragraph was already clean.
  // Undo still clears submitted state — treat that as success for the sweep.
  const alreadyClean = !hadSuffix && !stillHasSuffix;
  const result = {
    ok: Boolean(tableOk && rowsOk && (textChanged || alreadyClean)),
    clauseNumber,
    clauseId: afterCard.getAttribute('data-clause-id'),
    checks: { tableOk, rowsOk, textChanged, alreadyClean, hadSuffix, stillHasSuffix },
    beforeUndo,
    afterUndo,
  };
  log(`${PREFIX} undo-response`, result);
  return result;
}

async function runScanCards(args: { needle?: string }): Promise<Record<string, unknown>> {
  const needle = String(args.needle || '').trim();
  const needleFold = foldWs(needle);
  const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="cc-clause-card"]'));
  const hits: Array<{ clauseId: string; clauseNumber: string; head: string }> = [];
  for (const card of cards) {
    const text = foldWs(card.innerText || '');
    if (needleFold && text.includes(needleFold)) {
      hits.push({
        clauseId: card.getAttribute('data-clause-id') || '',
        clauseNumber: card.getAttribute('data-clause-number') || '',
        head: text.slice(0, 200),
      });
    }
  }
  return { needle, hitCount: hits.length, hits };
}

async function runSearch(args: { needle?: string }): Promise<Record<string, unknown>> {
  const needle = String(args.needle || '2500').slice(0, 80);
  if (typeof Word === 'undefined') return { office: false };
  return Word.run(async (context) => {
    const hits = context.document.body.search(needle, { matchCase: false });
    hits.load('items');
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load('items');
    await context.sync();
    const found = [];
    for (let i = 0; i < Math.min(hits.items.length, 8); i++) {
      const para = hits.items[i].paragraphs.getFirst();
      para.load('text');
      found.push(para);
    }
    for (let i = 0; i < paragraphs.items.length; i++) {
      paragraphs.items[i].load('text');
    }
    await context.sync();
    const needleFold = foldWs(needle);
    const scanned: Array<{ index: number; textLen: number; textHead: string; textTail: string }> = [];
    for (let i = 0; i < paragraphs.items.length; i++) {
      const text = foldWs(paragraphs.items[i].text || '');
      if (needleFold && text.includes(needleFold)) {
        scanned.push({
          index: i,
          textLen: text.length,
          textHead: text.slice(0, 100),
          textTail: text.slice(-80),
        });
      }
    }
    return {
      office: true,
      needle,
      hitCount: hits.items.length,
      scanHitCount: scanned.length,
      paragraphs: found.map((p) => {
        const text = foldWs(p.text || '');
        return { textLen: text.length, textHead: text.slice(0, 100), textTail: text.slice(-80) };
      }),
      scanned: scanned.slice(0, 8),
    };
  });
}

/**
 * Debug-loop primitives for the Skills chat flow (email-client-request-merge et al.).
 * Unlike the clause-card commands above, this flow has no stable data-testid hooks —
 * it is a plain chat thread (`.skill-runner-turn`) with dynamic option buttons
 * (`.skill-runner-option-btn`) and a shared composer (`.ag-composer-textarea` +
 * `.ag-send-circle`). These commands are generic DOM primitives (click by visible
 * text, set a controlled textarea, read the thread) so a new workflow step or a
 * fix iteration does not need a new bridge command each time.
 */

const SKILL_CLICKABLE_SELECTOR =
  '.skill-picker-card, .skill-runner-option-btn, .ag-mode-dropdown-item, .ag-mode-dropdown-trigger, button, [role="button"]';

function foldSkillText(text: string): string {
  return (text || '').replace(/\s+/g, ' ').trim();
}

function findClickableByText(needle: string, selector?: string): HTMLElement | null {
  const scope = selector || SKILL_CLICKABLE_SELECTOR;
  const needleFold = foldSkillText(needle);
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(scope));
  // Prefer the most specific (shortest text) match so a container button does not
  // shadow the actual target when both contain the needle.
  let best: HTMLElement | null = null;
  let bestLen = Infinity;
  for (const el of candidates) {
    const text = foldSkillText(el.innerText || el.textContent || '');
    if (!text || !text.includes(needleFold)) continue;
    if (text.length < bestLen) {
      best = el;
      bestLen = text.length;
    }
  }
  return best;
}

async function runSkillClickText(args: {
  needle?: string;
  selector?: string;
}): Promise<Record<string, unknown>> {
  const needle = String(args.needle || '');
  if (!needle.trim()) throw new Error('skill-click-text requires a non-empty needle');
  const el = await waitFor(() => findClickableByText(needle, args.selector), 10000, `clickable text ${JSON.stringify(needle)}`);
  el.scrollIntoView({ block: 'center' });
  await sleep(150);
  const fresh = findClickableByText(needle, args.selector) || el;
  fresh.click();
  log(`${PREFIX} skill-click-text`, { needle, matchedText: foldSkillText(fresh.innerText || '') });
  return { ok: true, needle, matchedText: foldSkillText(fresh.innerText || fresh.textContent || '') };
}

function setControlledInputValue(el: HTMLInputElement | HTMLTextAreaElement, text: string): void {
  const proto = el instanceof HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) {
    setter.call(el, text);
  } else {
    el.value = text;
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Fill a text field and submit it. Defaults to the shared chat composer
 * (`.ag-composer-textarea` + its `.ag-send-circle` send button) for paste text,
 * custom author, reposition instructions, etc. Pass `--selector` for any OTHER
 * field this flow needs text typed into — e.g. the placement wizard's inline
 * new-heading `<input>` (`.skill-runner-wizard-input-row input`), which is a
 * deliberately separate field from the shared composer (see
 * SkillRunner.tsx's wizard-card redesign: the whole wizard interaction stays
 * inside its own card, not routed through the bottom composer). For a
 * non-composer selector, submission is the nearest ancestor `<form>`'s submit
 * button rather than `.ag-send-circle`.
 */
async function runSkillPaste(args: {
  text?: string;
  selector?: string;
}): Promise<Record<string, unknown>> {
  const text = String(args.text || '');
  if (!text.trim()) throw new Error('skill-paste requires non-empty text');
  const selector = args.selector || '.ag-composer-textarea';
  const field = await waitFor(
    () => document.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector),
    10000,
    `text field ${selector}`,
  );
  field.focus();
  setControlledInputValue(field, text);
  await sleep(200);

  const isSharedComposer = selector === '.ag-composer-textarea';
  const submitBtn = isSharedComposer
    ? await waitFor(() => {
        const btn = document.querySelector<HTMLButtonElement>('.ag-send-circle');
        return btn && !btn.disabled ? btn : null;
      }, 5000, 'enabled send button')
    : await waitFor(() => {
        const form = field.closest('form');
        const btn = form?.querySelector<HTMLButtonElement>('button[type="submit"]');
        return btn && !btn.disabled ? btn : null;
      }, 5000, 'enabled submit button for custom field');
  submitBtn.click();
  log(`${PREFIX} skill-paste`, { selector, textLen: text.length, textHead: text.slice(0, 80) });
  return { ok: true, textLen: text.length };
}

/**
 * Snapshot of the Skills chat thread: every turn (role + text) and the currently
 * visible option buttons (`.skill-runner-option-btn`), so a debug loop can decide
 * the next click without guessing which step the workflow is on.
 */
async function runSkillState(): Promise<Record<string, unknown>> {
  const turns = Array.from(document.querySelectorAll<HTMLElement>('.skill-runner-turn')).map((el) => ({
    role: el.className.includes('skill-runner-turn--assistant') ? 'assistant' : 'user',
    text: foldSkillText(el.querySelector('.skill-runner-turn-content')?.textContent || el.textContent || ''),
  }));
  const options = Array.from(document.querySelectorAll<HTMLElement>('.skill-runner-option-btn')).map(
    (el) => foldSkillText(el.textContent || ''),
  );
  const composer = document.querySelector<HTMLTextAreaElement>('.ag-composer-textarea');
  const busy = Boolean(document.querySelector('.skill-runner-busy'));
  const error = foldSkillText(document.querySelector('.skill-runner-error')?.textContent || '');

  // The per-group placement wizard renders as a persistent step-through card
  // (`.skill-runner-wizard-card`), not as growing `.skill-runner-turn` pairs —
  // deliberately, so a lawyer sees ONE component stepping through N questions
  // instead of a chat log doubling in length per group. Surface its live
  // progress/question/mode here so a debug loop does not have to guess from
  // the (now wizard-question-silent) turns list which group is current.
  const wizardCard = document.querySelector<HTMLElement>('.skill-runner-wizard-card');
  const wizard = wizardCard
    ? {
        progress: foldSkillText(wizardCard.querySelector('.skill-runner-wizard-progress')?.textContent || ''),
        question: foldSkillText(wizardCard.querySelector('.skill-runner-wizard-question')?.textContent || ''),
        awaitingNewHeadingText: Boolean(wizardCard.querySelector('.skill-runner-wizard-input-row')),
        headingOptions: Array.from(
          wizardCard.querySelectorAll<HTMLElement>('.skill-runner-wizard-options .skill-runner-option-btn'),
        ).map((el) => foldSkillText(el.textContent || '')),
      }
    : null;

  return {
    ok: true,
    turnCount: turns.length,
    turns,
    lastAssistantTurn: [...turns].reverse().find((t) => t.role === 'assistant')?.text || '',
    options,
    wizard,
    composerVisible: Boolean(composer),
    busy,
    error: error || null,
  };
}

/**
 * Full non-empty body paragraph dump using the SAME sequential indexing the skill
 * engine plans against (AgentDocumentInserter maps target_paragraph_index to the
 * Nth non-empty paragraph) — so a before/after diff around an insertion index is
 * directly comparable to the engine's `target_paragraph_index`.
 */
async function runDumpParagraphs(args: { needle?: string }): Promise<Record<string, unknown>> {
  const paragraphs = await getDocumentParagraphsForSkill();
  const needle = foldSkillText(String(args.needle || ''));
  const filtered = needle
    ? paragraphs.filter((p) => foldSkillText(p.text).includes(needle))
    : paragraphs;
  return {
    ok: true,
    total: paragraphs.length,
    matched: filtered.length,
    paragraphs: filtered.map((p) => ({ index: p.index, text: p.text })),
  };
}

/**
 * Debug check for exactly the "does it just look like nothing happened"
 * concern: for each paragraph whose text matches `needle` (exact, bidi/list-
 * number-insensitive — same fold as AgentDocumentInserter.stripBidiMarks),
 * report whether Word actually carries tracked-change (w:ins) markup on it,
 * not just whether the plain text is present. Plain .text reads the resolved
 * content either way, so a text match alone does not prove the insertion is
 * visually marked as an addition.
 */
async function runCheckTrackedChanges(args: { needles: string[] }): Promise<Record<string, unknown>> {
  if (typeof Word === 'undefined') return { office: false };
  const needles = (args.needles || []).map((n) => foldWs(n));
  return Word.run(async (context) => {
    const body = context.document.body;
    const pc = body.paragraphs;
    pc.load('items');
    await context.sync();
    const items = pc.items as any[];
    for (const item of items) item.load('text');
    await context.sync();

    const results: Array<{
      needle: string;
      found: boolean;
      paragraphIndex: number | null;
      trackedChangeCount: number | null;
      trackedChangeTypes: string[];
    }> = [];

    for (const needle of needles) {
      let match: any = null;
      let matchWordIdx = -1;
      for (let wi = 0; wi < items.length; wi++) {
        const t = foldWs(String(items[wi]?.text ?? ''));
        // Only the paragraph's own text containing the needle counts — the
        // reverse direction (needle.includes(t)) is unsafe: an empty or very
        // short paragraph's text is trivially a substring of almost anything,
        // so it would "match" the first blank paragraph for every needle.
        if (t.length > 0 && t.includes(needle)) {
          match = items[wi];
          matchWordIdx = wi;
          break;
        }
      }
      if (!match) {
        results.push({ needle, found: false, paragraphIndex: null, trackedChangeCount: null, trackedChangeTypes: [] });
        continue;
      }
      let trackedChangeCount: number | null = null;
      let trackedChangeTypes: string[] = [];
      try {
        const tracked = (match as any).getTrackedChanges?.();
        if (tracked) {
          tracked.load('items');
          await context.sync();
          const trackedItems = (tracked.items as any[]) || [];
          for (const ti of trackedItems) ti.load('type');
          if (trackedItems.length) await context.sync();
          trackedChangeCount = trackedItems.length;
          trackedChangeTypes = trackedItems.map((ti: any) => String(ti.type));
        }
      } catch {
        // getTrackedChanges unsupported on this host — leave as null, not false
      }
      results.push({
        needle,
        found: true,
        paragraphIndex: matchWordIdx,
        trackedChangeCount,
        trackedChangeTypes,
      });
    }
    return { ok: true, results };
  });
}

/**
 * Diagnostic for the placement wizard's new-heading structure fix
 * (AgentDocumentInserter.applyHeadingStructureToInsertedText): reports the
 * FIRST matching paragraph's real list membership — `isListItem`,
 * `listString` (the resolved visible number, e.g. "130." — empty/undefined
 * means Word renders no number for this specific paragraph even if it IS
 * nominally a list item, which is the actual criterion DocumentReader uses to
 * decide whether to prepend a number when building engine-facing paragraph
 * text; `isListItem` alone is not reliable evidence either way) and
 * `listLevel`. Added specifically to debug why a newly-inserted heading kept
 * showing a list number after a `detachFromList()` call that appeared to run
 * without error — inspect BOTH the genuine reference heading and the new
 * heading paragraph and diff their actual list state instead of guessing.
 */
async function runInspectParagraphList(args: {
  needle?: string;
  index?: number;
}): Promise<Record<string, unknown>> {
  if (typeof Word === 'undefined') return { office: false };
  const needle = foldWs(String(args.needle || ''));
  const rawIndex = args.index;
  if (!needle && rawIndex === undefined) {
    throw new Error('inspect-paragraph-list requires --needle or --index');
  }
  return Word.run(async (context) => {
    const body = context.document.body;
    const pc = body.paragraphs;
    pc.load('items');
    await context.sync();
    const items = pc.items as any[];
    for (const item of items) item.load('text');
    await context.sync();

    let match: any = null;
    let matchWordIdx = -1;
    if (rawIndex !== undefined) {
      if (rawIndex >= 0 && rawIndex < items.length) {
        match = items[rawIndex];
        matchWordIdx = rawIndex;
      }
    } else {
      for (let wi = 0; wi < items.length; wi++) {
        const t = foldWs(String(items[wi]?.text ?? ''));
        if (t.length > 0 && t.includes(needle)) {
          match = items[wi];
          matchWordIdx = wi;
          break;
        }
      }
    }
    if (!match) return { ok: true, found: false };

    match.load(['isListItem', 'style']);
    const li = match.listItemOrNullObject;
    li.load('isNullObject,listString,level');
    await context.sync();

    let trackedChangeCount: number | null = null;
    let trackedChangeTypes: string[] = [];
    try {
      const tracked = match.getTrackedChanges?.();
      if (tracked) {
        tracked.load('items');
        await context.sync();
        const trackedItems = (tracked.items as any[]) || [];
        for (const ti of trackedItems) ti.load('type');
        if (trackedItems.length) await context.sync();
        trackedChangeCount = trackedItems.length;
        trackedChangeTypes = trackedItems.map((ti: any) => String(ti.type));
      }
    } catch {
      // getTrackedChanges unsupported on this host — leave as null
    }

    return {
      ok: true,
      found: true,
      paragraphIndex: matchWordIdx,
      text: String(match.text ?? ''),
      isListItem: !!match.isListItem,
      style: String(match.style ?? ''),
      listItemIsNullObject: !!li.isNullObject,
      listString: li.isNullObject ? null : String(li.listString ?? ''),
      listLevel: li.isNullObject ? null : li.level,
      trackedChangeCount,
      trackedChangeTypes,
    };
  });
}

/**
 * Debug-loop cleanup: reject every tracked change in the document body via
 * Office.js directly, instead of the AppleScript-driven Edit-menu Undo click
 * loop (`restore-original`) — that mechanism can get stuck reporting
 * `undone: 0` indefinitely (focus/timing flakiness), and once a batch is
 * confirmed to be genuine tracked "Added" insertions (see
 * check-tracked-changes), rejecting them is an equally clean revert that does
 * not depend on native UI automation at all.
 */
async function runRejectAllTrackedChanges(): Promise<Record<string, unknown>> {
  if (typeof Word === 'undefined') return { office: false };
  return Word.run(async (context) => {
    const tracked = context.document.body.getTrackedChanges();
    tracked.load('items');
    await context.sync();
    const items = (tracked.items as any[]) || [];
    const count = items.length;
    for (const item of items) {
      try {
        item.reject();
      } catch {
        // best-effort — a change that fails to reject individually is reported
        // via the before/after paragraph count, not swallowed silently
      }
    }
    await context.sync();
    return { ok: true, rejected: count };
  });
}

async function handleCommand(cmd: { id: string; action: string; args?: Record<string, unknown> }): Promise<unknown> {
  const clauseNumber = String(cmd.args?.clauseNumber || '7.9');
  if (cmd.action === 'ping') {
    const text = (document.body.innerText || '').replace(/\s+/g, ' ').trim();
    const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="cc-clause-card"]'));
    return {
      agentBridge: 'accepted-ooxml-v1',
      applyVerify: 'accepted-ooxml-v1',
      cards: cards.length,
      cardIds: cards.map((c) => c.getAttribute('data-clause-id')),
      cardNumbers: cards.map((c) => c.getAttribute('data-clause-number')),
      match79: !!findCard('7.9'),
      match79id: findCard('7.9')?.getAttribute('data-clause-id') || null,
      title: document.title,
      heading: document.querySelector('h1, h2, h3')?.textContent?.trim() || '',
      bodyHead: text.slice(0, 400),
    };
  }
  if (cmd.action === 'snapshot') return runSnapshot({ clauseNumber });
  if (cmd.action === 'search') return runSearch({ needle: String(cmd.args?.needle || '2500') });
  if (cmd.action === 'scan-cards') return runScanCards({ needle: String(cmd.args?.needle || '') });
  if (cmd.action === 'restore-baseline') {
    throw new Error('restore-baseline is disabled: never rewrite the cell. Use Word Undo until exhausted.');
  }
  if (cmd.action === 'clear') return runClear({ clauseNumber });
  if (cmd.action === 'quick-response') {
    return runQuickResponse({
      clauseNumber,
      type: String(cmd.args?.type || 'see_proposed_correction'),
    });
  }
  if (cmd.action === 'ai') return runAi({ clauseNumber });
  if (cmd.action === 'submit') {
    return runSubmit({
      clauseNumber,
      baselineText: String(cmd.args?.baselineText || ''),
    });
  }
  if (cmd.action === 'undo-response') return runUndoResponse({ clauseNumber });
  if (cmd.action === 'manual-edit-submit') {
    return runManualEditSubmit({ clauseNumber, text: String(cmd.args?.text || '') });
  }
  if (cmd.action === 'probe-beforeinput') return runProbeBeforeinput({ clauseNumber });
  if (cmd.action === 'primed-paste-dry-run') {
    return runPrimedPasteDryRun({ clauseNumber, text: String(cmd.args?.text || '') });
  }
  if (cmd.action === 'skill-click-text') {
    return runSkillClickText({
      needle: String(cmd.args?.needle || ''),
      selector: cmd.args?.selector ? String(cmd.args.selector) : undefined,
    });
  }
  if (cmd.action === 'skill-paste') {
    return runSkillPaste({
      text: String(cmd.args?.text || ''),
      selector: cmd.args?.selector ? String(cmd.args.selector) : undefined,
    });
  }
  if (cmd.action === 'skill-state') return runSkillState();
  if (cmd.action === 'dump-paragraphs') {
    return runDumpParagraphs({ needle: String(cmd.args?.needle || '') });
  }
  if (cmd.action === 'check-tracked-changes') {
    const raw = cmd.args?.needles;
    const needles = Array.isArray(raw) ? raw.map((n) => String(n)) : [String(cmd.args?.needle || '')];
    return runCheckTrackedChanges({ needles });
  }
  if (cmd.action === 'reject-all-tracked-changes') return runRejectAllTrackedChanges();
  if (cmd.action === 'inspect-paragraph-list') {
    const rawIndex = cmd.args?.index;
    return runInspectParagraphList({
      needle: String(cmd.args?.needle || ''),
      index: rawIndex !== undefined && rawIndex !== null && rawIndex !== '' ? Number(rawIndex) : undefined,
    });
  }
  if (cmd.action === 'apply-heading-structure') {
    // Debug-only direct call into AgentDocumentInserter.applyHeadingStructureToInsertedText
    // against whatever is CURRENTLY live in the document — lets iterating on that
    // function's fix skip the full paste→wizard→execute cycle each time.
    const target = String(cmd.args?.target || '');
    const reference = String(cmd.args?.reference || '');
    if (!target || !reference) throw new Error('apply-heading-structure requires --target and --reference');
    await applyHeadingStructureToInsertedText([target], reference);
    return { ok: true };
  }
  throw new Error(`unknown action: ${cmd.action}`);
}

type AgentWindow = Window & {
  __courtyardMccAgentStop?: () => void;
  __courtyardMccAgentHandleCommand?: typeof handleCommand;
};

export function installMccAgentBridge(): void {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV === 'production') return;

  const w = window as unknown as AgentWindow;
  w.__courtyardMccAgentHandleCommand = handleCommand;
  if (w.__courtyardMccAgentStop) {
    w.__courtyardMccAgentStop();
    w.__courtyardMccAgentStop = undefined;
  }

  let stopped = false;
  const loop = async () => {
    while (!stopped) {
      try {
        const response = await fetch('/__agent__/poll?v=accepted-ooxml-v1', { cache: 'no-store' });
        if (response.status === 204) continue;
        if (!response.ok) {
          await sleep(2000);
          continue;
        }
        const cmd = (await response.json()) as { id: string; action: string; args?: Record<string, unknown> };
        log(`${PREFIX} got command`, { id: cmd.id, action: cmd.action });
        const handler = w.__courtyardMccAgentHandleCommand || handleCommand;
        try {
          const result = await handler(cmd);
          await fetch('/__agent__/result', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: cmd.id, ok: true, result }),
          });
        } catch (err) {
          await fetch('/__agent__/result', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: cmd.id,
              ok: false,
              error: err instanceof Error ? err.message : String(err),
            }),
          });
        }
      } catch {
        await sleep(2000);
      }
    }
  };
  void loop();
  w.__courtyardMccAgentStop = () => {
    stopped = true;
  };
}

if (typeof window !== 'undefined') {
  (window as unknown as AgentWindow).__courtyardMccAgentHandleCommand = handleCommand;
}
